import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { client } from '../amplify-client';
import { GuennieRating } from '../components/GuennieRating';
import { KriteriumAnzeige } from '../components/KriteriumAnzeige';
import { SpaghettiDivider } from '../components/SpaghettiDivider';
import { useFotoVarianteUrl } from '../lib/useFotoUrl';
import { formatDatum, formatEuro, SERVIERT_ALS_LABEL, WIEDERKOMMEN_LABEL } from '../lib/format';
import { ALLE_KRITERIEN, KRITERIUM_LABEL, type Kriterium } from '../lib/kriterien';
import type { Schema } from '../../amplify/data/resource';

type Test = Schema['SpaghettiEisTest']['type'];
type Eisdiele = Schema['Eisdiele']['type'];

export function VerkostungDetail() {
  const { id } = useParams<{ id: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [eisdiele, setEisdiele] = useState<Eisdiele | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fotoFallback, setFotoFallback] = useState(false);
  // Zeigt die Web-Variante (Bauphase 4, sharp) — deutlich kleiner als das
  // Original. Ist sie ausnahmsweise noch nicht fertig (Lambda braucht einen
  // Moment nach dem Upload), fällt der <img>-onError-Handler unten aufs
  // Original zurück.
  const { url: fotoUrl, originalUrl } = useFotoVarianteUrl(test?.id, test?.fotoKey, 'web');

  useEffect(() => {
    if (!id) return;
    setLaedt(true);
    client.queries
      .holeVeroeffentlichtenTest({ id })
      .then(async ({ data }) => {
        if (!data) {
          setFehler('Diese Verkostung ist nicht (mehr) veröffentlicht.');
          return;
        }
        setTest(data as Test);
        const { data: e } = await client.models.Eisdiele.get({ id: data.eisdieleId });
        setEisdiele(e ?? null);
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, [id]);

  if (laedt) return <div className="container">Lädt …</div>;
  if (fehler || !test) {
    return (
      <div className="container">
        <p className="hinweis">{fehler ?? 'Nicht gefunden.'}</p>
        <Link to="/app/verkostungen">← zurück zur Liste</Link>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '46rem' }}>
      <p className="label">
        <Link to="/app/verkostungen">← Alle Verkostungen</Link>
      </p>

      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {(fotoFallback ? originalUrl : fotoUrl) ? (
          <img
            src={fotoFallback ? originalUrl! : fotoUrl!}
            onError={() => {
              if (!fotoFallback) setFotoFallback(true);
            }}
            alt={`Spaghetti-Eis bei ${eisdiele?.name ?? ''}`}
            style={{
              width: '16rem',
              maxWidth: '100%',
              aspectRatio: '1 / 1',
              objectFit: 'cover',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--raspel)',
            }}
          />
        ) : null}
        <div style={{ flex: 1, minWidth: '14rem' }}>
          <p className="label">Verkostung</p>
          <h1 style={{ marginBottom: '0.2rem' }}>
            {eisdiele ? (
              <Link to={`/app/eisdielen/${eisdiele.id}`} style={{ color: 'inherit' }}>
                {eisdiele.name}
              </Link>
            ) : (
              'Unbekannte Eisdiele'
            )}
          </h1>
          <p style={{ color: 'var(--espresso-70)' }}>
            {eisdiele?.ort} · {formatDatum(test.besuchsdatum)} · von {test.testerName}
          </p>
          <div style={{ marginTop: '0.75rem' }}>
            <div className="label">Gesamt</div>
            <GuennieRating wert={test.gesamtGuennies} groesse="lg" />
          </div>
        </div>
      </div>

      <SpaghettiDivider />

      <h2>Auf dem Teller</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))',
          gap: '0.75rem',
          marginBottom: '1.5rem',
        }}
      >
        {test.variante ? <Kachel label="Variante" wert={test.variante} /> : null}
        <Kachel label="Portionspreis" wert={formatEuro(test.portionspreisEuro)} />
        {test.serviertAls ? (
          <Kachel label="Serviert als" wert={SERVIERT_ALS_LABEL[test.serviertAls] ?? test.serviertAls} />
        ) : null}
        {test.wartezeitMin != null ? <Kachel label="Wartezeit" wert={`${test.wartezeitMin} Min.`} /> : null}
        {test.wiederkommen ? (
          <Kachel label="Wiederkommen?" wert={WIEDERKOMMEN_LABEL[test.wiederkommen] ?? test.wiederkommen} />
        ) : null}
      </div>

      <h2>Bewertung im Detail</h2>
      <div className="karte" style={{ marginBottom: '1.5rem' }}>
        {ALLE_KRITERIEN.map((k) => (
          <KriteriumAnzeige key={k} label={KRITERIUM_LABEL[k as Kriterium]} wert={test[k as Kriterium]} />
        ))}
      </div>

      {test.fazit ? (
        <>
          <p className="label">Verkostungsnotiz</p>
          <p style={{ fontSize: '1.05rem' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.6em', float: 'left', lineHeight: 1, marginRight: '0.2rem' }}>
              {test.fazit.charAt(0)}
            </span>
            {test.fazit.slice(1)}
          </p>
        </>
      ) : null}
    </div>
  );
}

function Kachel({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="karte" style={{ padding: '0.6rem 0.8rem' }}>
      <div className="label">{label}</div>
      <div style={{ fontWeight: 600 }}>{wert}</div>
    </div>
  );
}
